import React, { useState, useEffect } from "react";
import { clientsAPI, projectsAPI, tasksAPI, timeEntriesAPI } from "../lib/api";
import { useTimer } from "../contexts/TimerContext";
import { formatDate, formatDateTime, formatDuration, formatDurationLong } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Play, Square, Plus, Clock, MoreHorizontal, Pencil, Trash2, X } from "lucide-react";

export default function TimeTrackerPage() {
  const { activeTimers, startTimer, stopTimer, discardTimer, formatElapsedForClient } = useTimer();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Timer form
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedTask, setSelectedTask] = useState("");
  const [description, setDescription] = useState("");

  // Manual entry dialog
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({
    client_id: "",
    project_id: "",
    task_id: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    start_time: "09:00",
    end_time: "10:00",
    billable: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchProjectsAndTasks(selectedClient);
    } else {
      setProjects([]);
      setTasks([]);
    }
  }, [selectedClient]);

  const fetchData = async () => {
    try {
      const [clientsRes, entriesRes] = await Promise.all([
        clientsAPI.list({ status: "active" }),
        timeEntriesAPI.list(),
      ]);
      setClients(clientsRes.data);
      setTimeEntries(entriesRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectsAndTasks = async (clientId) => {
    try {
      const [projectsRes, tasksRes] = await Promise.all([
        projectsAPI.list({ client_id: clientId }),
        tasksAPI.list({ client_id: clientId }),
      ]);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
    } catch (err) {
      console.error("Error fetching projects/tasks:", err);
    }
  };

  const handleStartTimer = async () => {
    if (!selectedClient) {
      toast.error("Please select a client");
      return;
    }
    try {
      await startTimer({
        client_id: selectedClient,
        project_id: selectedProject && selectedProject !== "none" ? selectedProject : null,
        task_id: selectedTask && selectedTask !== "none" ? selectedTask : null,
        description: description || null,
      });
      toast.success("Timer started");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start timer");
    }
  };

  const handleStopTimer = async (clientId) => {
    try {
      await stopTimer(clientId);
      toast.success("Time entry saved");
      fetchData();
    } catch (err) {
      toast.error("Failed to stop timer");
    }
  };

  const handleDiscardTimer = async (clientId) => {
    if (!window.confirm("Discard current timer?")) return;
    try {
      await discardTimer(clientId);
      toast.success("Timer discarded");
    } catch (err) {
      toast.error("Failed to discard timer");
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      const startDateTime = new Date(`${manualForm.date}T${manualForm.start_time}:00`);
      const endDateTime = new Date(`${manualForm.date}T${manualForm.end_time}:00`);
      const durationMinutes = Math.floor((endDateTime - startDateTime) / 60000);

      if (durationMinutes <= 0) {
        toast.error("End time must be after start time");
        return;
      }

      await timeEntriesAPI.create({
        client_id: manualForm.client_id,
        project_id: manualForm.project_id || null,
        task_id: manualForm.task_id || null,
        description: manualForm.description || null,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        duration_minutes: durationMinutes,
        billable: manualForm.billable,
      });
      toast.success("Time entry added");
      setShowManualDialog(false);
      setManualForm({
        client_id: "",
        project_id: "",
        task_id: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        start_time: "09:00",
        end_time: "10:00",
        billable: true,
      });
      fetchData();
    } catch (err) {
      toast.error("Failed to add time entry");
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Delete this time entry?")) return;
    try {
      await timeEntriesAPI.delete(id);
      toast.success("Time entry deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || "-";

  // Today's stats
  const today = new Date().toISOString().split("T")[0];
  const todayEntries = timeEntries.filter(e => e.start_time?.startsWith(today));
  const todayMinutes = todayEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);

  // This week stats
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekEntries = timeEntries.filter(e => new Date(e.start_time) >= weekStart);
  const weekMinutes = weekEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="time-tracker-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Time Tracker</h1>
        <p className="text-muted-foreground mt-1">
          Track your time and manage entries
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Today</p>
            <p className="text-2xl font-bold mt-1 font-mono">{formatDurationLong(todayMinutes)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">This Week</p>
            <p className="text-2xl font-bold mt-1 font-mono">{formatDurationLong(weekMinutes)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Entries Today</p>
            <p className="text-2xl font-bold mt-1">{todayEntries.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Entries</p>
            <p className="text-2xl font-bold mt-1">{timeEntries.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Running Timers */}
      {activeTimers.length > 0 && (
        <Card className="border-brand-accent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Running Timers ({activeTimers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {activeTimers.map((timer) => (
              <div key={timer.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="font-medium">{getClientName(timer.client_id)}</p>
                  {timer.description && (
                    <p className="text-sm text-muted-foreground">{timer.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <p className="timer-display timer-running font-mono text-lg" data-testid="timer-display">
                    {formatElapsedForClient(timer.client_id)}
                  </p>
                  <Button size="sm" onClick={() => handleStopTimer(timer.client_id)} className="gap-2" data-testid="stop-timer-btn">
                    <Square className="h-4 w-4 fill-current" />
                    Stop & Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDiscardTimer(timer.client_id)} data-testid="discard-timer-btn">
                    <X className="h-4 w-4 mr-2" />
                    Discard
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Start New Timer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Start New Timer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="timer-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject} disabled={!selectedClient}>
                <SelectTrigger data-testid="timer-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Task</Label>
              <Select value={selectedTask} onValueChange={setSelectedTask} disabled={!selectedClient}>
                <SelectTrigger data-testid="timer-task-select">
                  <SelectValue placeholder="Select task" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Task</SelectItem>
                  {tasks.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What are you working on?"
                data-testid="timer-description-input"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <Button onClick={handleStartTimer} className="gap-2" data-testid="start-timer-btn">
              <Play className="h-4 w-4" />
              Start Timer
            </Button>
            <Button variant="outline" onClick={() => setShowManualDialog(true)} className="gap-2" data-testid="manual-entry-btn">
              <Plus className="h-4 w-4" />
              Manual Entry
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Time Entries */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Time Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {timeEntries.length === 0 ? (
            <div className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No time entries yet</p>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Billable</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeEntries.slice(0, 20).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{formatDateTime(entry.start_time)}</TableCell>
                      <TableCell>{getClientName(entry.client_id)}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{entry.description || "-"}</TableCell>
                      <TableCell className="font-mono">{formatDuration(entry.duration_minutes)}</TableCell>
                      <TableCell>
                        <Badge variant={entry.billable ? "default" : "secondary"}>
                          {entry.billable ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDeleteEntry(entry.id)} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Entry Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Time Entry</DialogTitle>
            <DialogDescription>
              Record time that was tracked outside of the app
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select
                value={manualForm.client_id}
                onValueChange={(v) => setManualForm({ ...manualForm, client_id: v, project_id: "", task_id: "" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={manualForm.description}
                onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                placeholder="What did you work on?"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={manualForm.date}
                  onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={manualForm.start_time}
                  onChange={(e) => setManualForm({ ...manualForm, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={manualForm.end_time}
                  onChange={(e) => setManualForm({ ...manualForm, end_time: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="billable"
                checked={manualForm.billable}
                onChange={(e) => setManualForm({ ...manualForm, billable: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="billable">Billable</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowManualDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!manualForm.client_id}>
                Add Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
